import { haptic } from "@/lib/haptic";
import React from "react";
import { motion } from "framer-motion";
import { NavVariantContext } from "./NavVariantContext";

interface NavButtonProps {
  isActive: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  fillIcon: React.ReactNode;
  text: string;
  className?: string;
  /** Optional numeric badge bubble on the icon (e.g. Dictionary review count). Hidden when 0. */
  badge?: number;
}

const NavButton: React.FC<NavButtonProps> = ({
  isActive,
  onClick,
  icon,
  fillIcon,
  text,
  className,
  badge,
}) => {
  const variant = React.useContext(NavVariantContext);
  const isDark = variant === "dark";
  const handleClick = () => {
    haptic.soft();
    onClick();
  };
  const showBadge = typeof badge === "number" && badge > 0;
  // iOS dark feed nav (Liquid Glass): active icon stays BLUE (like the light
  // bar), inactive icons are WHITE OUTLINES (not gray). Light bar keeps the
  // blue/grey tint. Driven via inline color so it tweens with the bar's
  // background under transition-colors.
  const tintColor = isDark
    ? isActive
      ? "var(--color-blue)"
      : "rgba(255,255,255,0.92)"
    : isActive
      ? "var(--color-blue)"
      : "var(--color-grey)";
  return (
    <motion.button
      onClick={handleClick}
      // Press squish: a snappy spring scale-down on tap for a tactile response.
      whileTap={{ scale: 0.86 }}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
      // z-20 keeps the icon/label above the NavBar's gray selection circle.
      // transition-colors duration-500 animates the light↔dark variant swap.
      className={`relative z-20 flex-1 rounded-2xl transition-colors duration-500 ease-in-out ${className}`}
      style={{ color: tintColor }}
    >
      <div
        className={`relative flex flex-col items-center justify-center gap-[2px] w-full px-[6px] py-[5px]`}
      >
        {/* data-nav-icon: measured by NavBar to place the gray selection circle. */}
        <div data-nav-icon className="relative h-[22px] w-[22px]">
          {isActive ? fillIcon : icon}
          {showBadge && (
            <span
              className="absolute -top-[5px] -right-[7px] min-w-[16px] h-[16px] px-[4px] rounded-full bg-[var(--color-red)] text-white flex items-center justify-center font-semibold leading-none"
              style={{ fontSize: "10px" }}
            >
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </div>
        <p className="text-xs">{text}</p>
      </div>
    </motion.button>
  );
};

export default NavButton;
